<script setup lang="ts">

import { useRememberMe } from '@/composables/useRememberMe';
import BravoLoginScreenSSO from '@services/ui-component-library/components/BravoLoginScreenSSO.vue';
import BravoTag from '@services/ui-component-library/components/BravoTag.vue';
import BravoDialog from '@services/ui-component-library/components/BravoDialog.vue';
import BravoButton from '@services/ui-component-library/components/BravoButton.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import SsoProviderDialog from '../components/SsoProviderDialog.vue';
import { useAuthStore } from '../stores/auth';
import { useSSO } from '@/composables/services/useSSO';

const router = useRouter();
const authStore = useAuthStore();
const { checkSSO } = useSSO();
const { rememberedEmail, isRemembered, handleRememberMe } = useRememberMe();
const bravoLoginRef = ref<InstanceType<typeof BravoLoginScreenSSO>>();
const { locale, t } = useI18n();

// SSO state
const ssoProviders = ref<Array<{logo: string | null; name: string; description: string; url: string}>>([]);
const showSsoProviderDialog = ref(false);

// Logged out modal state
const showLoggedOutPopup = ref(false)
const loggingOut = ref(false);

onMounted(() => {
    // If appv5 is in an iframe on another origin, the session cookie is stored in the parent's partition
    // and will not be sent from this frame—login may succeed but later API calls can get 401. Open appv5 in its own tab to log in.
    if (typeof window !== 'undefined' && window.self !== window.top) {
        console.warn(
            '[CXME] Login is running inside an iframe. Session cookies may not be sent on API requests due to cookie partitioning. For reliable login, open appv5 in its own tab.'
        );
    }

    // Check for logged out state from sessionStorage (clears when tab is closed)
    const showLoggedOutFlag = sessionStorage.getItem('showLoggedOutPopup');
    
    if (showLoggedOutFlag === 'true') {
        showLoggedOutPopup.value = true;
        
        // If user was logged out due to timeout, set up redirect to their previous location
        const timeoutRedirectPath = sessionStorage.getItem('redirectAfterTimeoutLogin');
        if (timeoutRedirectPath) {
            authStore.redirectAfterLogin = timeoutRedirectPath;
        }
    }

    // Set remembered email if available
    setTimeout(() => {
        const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
        if (emailInput) {
            // If we have a remembered email, set it in the input first
            if (rememberedEmail.value) {
                emailInput.value = rememberedEmail.value;
                
                // Trigger all the events the component needs to recognize the change
                emailInput.dispatchEvent(new Event('input', { bubbles: true }));
                emailInput.dispatchEvent(new Event('change', { bubbles: true }));
                emailInput.dispatchEvent(new Event('blur', { bubbles: true }));
            }
        }
        
        // Auto-check the remember me checkbox if user is remembered
        if (isRemembered.value) {
            const rememberMeCheckbox = document.querySelector('input[type="checkbox"]') as HTMLInputElement;
            if (rememberMeCheckbox && !rememberMeCheckbox.checked) {
                rememberMeCheckbox.checked = true;
                // Trigger change event to ensure component state is updated
                rememberMeCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
                rememberMeCheckbox.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }, 100);
});

// Email check handler for BravoLoginScreenSSO
// Returns false if SSO is not available, otherwise returns whether internal auth is available
const emailCheckHandler = async (email: string): Promise<boolean> => {
    // Test cookie setting here
    authStore.setRememberMeCookie();
    
    // Store email for remember me functionality
    if (email) {
        handleRememberMe(email, true);
    }
    
    // SSO check disabled temporarily
    return false;

    // try {
    //     const response = await checkSSO(email);
    //     
    //     // Store SSO providers for later use, adding the app parameter to URLs
    //     ssoProviders.value = (response.externalIdPs || []).map((provider: { logo: string | null; name: string; description: string; url: string }) => ({
    //         ...provider,
    //         url: addAppParameterToUrl(provider.url)
    //     }));
    //     // Return false if external auth is not available
    //     if (response.canAuthenticateExternal) {
    //         return true;
    //     }
    //     return false;
    // } catch (error) {
    //     console.error('Error checking SSO:', error);
    //     console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack trace available');
    //     
    //     // Throw error to prevent user from progressing and show error in login component
    //     throw new Error('Unable to login, please refresh and try again');
    // }
};

// Helper function to add X-Boomtown-App parameter to SSO URLs (as query param).
// When VITE_APP_ENV is stage/preprod/prod, force the redirect to VITE_SSO_APP_ORIGIN so SSO
// goes to the app host that serves /api/sso/saml. When local or unset, use current origin.
const addAppParameterToUrl = (url: string): string => {
    if (!url || url.trim() === '') {
        console.error('Invalid URL:', url, new Error('Empty URL'));
        return url;
    }
    const appEnv = import.meta.env.VITE_APP_ENV as string | undefined;
    const ssoAppOrigin = (import.meta.env.VITE_SSO_APP_ORIGIN as string || '').replace(/\/$/, '');
    const useSsoAppOrigin = ssoAppOrigin && appEnv !== 'local';
    const base = useSsoAppOrigin ? ssoAppOrigin : (typeof window !== 'undefined' ? window.location.origin : 'https://localhost');

    let urlObj: URL;
    let isRelative = false;
    try {
        try {
            urlObj = new URL(url);
        } catch {
            if (url.startsWith('/')) {
                urlObj = new URL(url, base);
                isRelative = true;
            } else {
                const err = new Error('Invalid URL');
                console.error('Invalid URL:', url, err);
                return url;
            }
        }
        const isOurAppUrl = isRelative || urlObj.pathname.startsWith('/api/sso') || /appv5|ovationcxm/i.test(urlObj.hostname);
        if (useSsoAppOrigin && isOurAppUrl) {
            urlObj = new URL(urlObj.pathname + urlObj.search + (urlObj.hash || ''), ssoAppOrigin);
        }
        urlObj.searchParams.set('x-boomtown-app', 'cxme');
        return urlObj.toString();
    } catch (error) {
        console.error('Invalid URL:', url, error);
        return url;
    }
};

// Handle SSO redirect event from BravoLoginScreenSSO
const handleSsoRedirect = (eventData: { email: string }) => {
    console.debug('handle sso redirect');
    // If we have multiple SSO providers, show the selection dialog
    if (ssoProviders.value.length > 1) {
        showSsoProviderDialog.value = true;
    } 
    // If we have only one SSO provider, redirect directly
    else if (ssoProviders.value.length === 1) {
        window.location.href = ssoProviders.value[0].url;
    }
    // Otherwise, show error (this shouldn't happen if component is working correctly)
    console.error('No SSO providers available');
};

// Handle provider selection from dialog
const handleProviderSelected = (url: string) => {
    window.location.href = url;
};

const handleSubmit = async (credentials: { email: string; password: string; rememberMe: boolean }) => {
    try {
        // Call the login method from the user store with the provided credentials
        await authStore.login({
            email: credentials.email,
            password: credentials.password,
            rememberMe: credentials.rememberMe,
        });

        // Handle remember me based on checkbox state on successful login
        handleRememberMe(credentials.email, credentials.rememberMe);

        // Clean up timeout redirect flag after successful login
        sessionStorage.removeItem('redirectAfterTimeoutLogin');

        // If login successful, redirect to home
        // check auth store for redirect after login
        // otherwise go to inbox view home
        if (authStore.redirectAfterLogin) {
            console.log('Login: Redirecting to saved location:', authStore.redirectAfterLogin);
            
            // If redirect after login is a full URL, redirect to it directly rather than attempting to route it
            if (authStore.redirectAfterLogin.startsWith('https')) {
                window.location.href = authStore.redirectAfterLogin;
                authStore.redirectAfterLogin = '';
            } else {
                // Validate redirect path before navigating to prevent circular navigation
                const isValidRedirect = authStore.redirectAfterLogin !== '/login' && 
                                       !authStore.redirectAfterLogin.startsWith('/login?') &&
                                       authStore.redirectAfterLogin !== '/passwordreset' && 
                                       !authStore.redirectAfterLogin.startsWith('/passwordreset?');
                
                if (isValidRedirect) {
                    try {
                        await router.push(authStore.redirectAfterLogin);
                        authStore.redirectAfterLogin = '';
                    } catch (error) {
                        // Log navigation failure as error for DataDog tracking
                        console.error('Login: Redirect navigation failed:', {
                            redirectPath: authStore.redirectAfterLogin,
                            error: error instanceof Error ? error.message : 'Unknown error'
                        });
                        
                        // Clear the failed redirect and fall back to inbox home
                        authStore.redirectAfterLogin = '';
                        await router.push('/inbox?view=home');
                    }
                } else {
                    // Invalid redirect path, clear it and go to inbox home
                    console.log('Login: Invalid redirect path, going to inbox home instead');
                    authStore.redirectAfterLogin = '';
                    await router.push('/inbox?view=home');
                }
            }
        } else {
            console.log('Login: No redirect path, going to inbox home');
            await router.push('/inbox?view=home');
        }
    } catch (error) {
        // Handle different error types with specific messages
        let errorMessage = 'Login attempt failed. Please try again';
        
        if (error instanceof Error && error.message) {
            // Check if this is an HTTP error with status code
            const httpErrorMatch = error.message.match(/HTTP error! status: (\d+)/);
            if (httpErrorMatch) {
                const statusCode = parseInt(httpErrorMatch[1]);
                // Check if it's a 5xx server error
                if (statusCode >= 500 && statusCode < 600) {
                    errorMessage = 'We are currently experiencing system issues. Please try again in a few minutes.';
                }
            }
        }
        
        // Re-throw the error with the appropriate message for the BravoLoginScreenSSO component to display
        throw new Error(errorMessage);
    }
};

const handleForgotPassword = (eventData: { email: string }) => {
    if (eventData?.email) {
        router.push({ path: '/passwordreset', query: { email: eventData.email } });
    } else {
        router.push('/passwordreset');
    }
};

// Handle closing the logged out modal
const handleCloseLoggedOutModal = () => {
    showLoggedOutPopup.value = false;
    // Clear the sessionStorage flags so they don't show again
    sessionStorage.removeItem('showLoggedOutPopup');
    // Keep the redirect path - it will be used when user logs in and then cleaned up
};
</script>

<template>
    <div class="login-page" data-testid="login-page">
        <BravoTag severity="warn">Beta</BravoTag>
        
        <div class="logo-container" data-testid="logo-container">
            <img src="../assets/ovationcxm-login-logo.png" alt="OvationCXM Logo" data-testid="logo-image" />
        </div>

        <!-- Login is fetch/XHR via submitHandler (no form action); address bar stays on appv5 so session cookie partition is correct. See README "Session and Boomtown API". -->
        <BravoLoginScreenSSO 
            ref="bravoLoginRef" 
            :submitHandler="handleSubmit"
            :emailCheckHandler="emailCheckHandler"
            :initialEmail="rememberedEmail"
            :initialRememberMe="isRemembered"
            @ssoRedirect="handleSsoRedirect"
            data-testid="bravo-login-screen" 
            @forgotPassword="handleForgotPassword"

        />

        <!-- SSO Provider Selection Dialog -->
        <SsoProviderDialog
            v-model:visible="showSsoProviderDialog"
            :providers="ssoProviders"
            @provider-selected="handleProviderSelected"
        />

        <!-- Logged Out Modal -->
        <BravoDialog 
            v-if="showLoggedOutPopup"
            :visible="showLoggedOutPopup"
            modal 
            :header="'You\'ve Been Logged Out'"
            :style="{ width: '400px' }"
            :closable="false"
            data-testid="logged-out-popup-modal"
            class="logged-out-dialog"
            :loading="loggingOut"
        >
            <div class="logged-out-content">
                <p class="confirmation-message">
                    {{ t('Your session has been expired due to inactivity.') }}
                </p>
            </div>
        
            <template #footer>
                <div class="dialog-footer">
                    <BravoButton 
                        class="p-button" 
                        @click="handleCloseLoggedOutModal"
                        severity="danger"
                        :label="t('nav.login')"
                    />
                </div>
            </template>
        </BravoDialog>
    </div>
</template>

<style scoped>
.login-page {
    background-image: url('../assets/login-ovationcxm-background.svg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
}

.logo-container {
    max-width: 300px;
}

.logo-container img {
    width: 100%;
    height: auto;
}

/* Override the BravoLoginScreenSSO's wrapper background */
:deep(.login-wrapper) {
    position: relative !important;
    background: transparent !important;
}

/* Logged out modal styling - matches original design */
.logged-out-content {
    padding: 0;
}

.confirmation-message {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: var(--text-color-primary);
}

.sub-message {
    margin: 0;
    font-size: 0.875rem;
    color: var(--text-color-secondary);
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
